import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useCan } from "@/lib/auth/useCan";

import type { AccountDetailLink } from "../../api";

import { AccountLinkForm } from "./AccountLinkForm";
import { RemoveLinkDialog } from "./RemoveLinkDialog";

const REL_LABEL_OUT: Record<string, string> = {
  merged_into: "Merged into",
  split_from: "Split from",
  subcustomer_of: "Sub-customer of",
  continuation_of: "Continuation of",
};
const REL_LABEL_IN: Record<string, string> = {
  merged_into: "Absorbed from",
  split_from: "Split off into",
  subcustomer_of: "Hosts sub-customer",
  continuation_of: "Continued by",
};

/**
 * Section card on the expanded account view. Admins can add new links and
 * remove existing ones. Sub-customer relationships intentionally live on the
 * Partners feature and aren't editable here — matches the old admin app.
 */
export function AccountLinksSection({
  qbId,
  links,
}: {
  qbId: number;
  links: AccountDetailLink[];
}) {
  const canAdmin = useCan("renewals.admin");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<AccountDetailLink | null>(null);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>Account links</CardTitle>
          <CardDescription>
            Merges, splits, and continuations. Sub-customer wiring is managed in Partners.
          </CardDescription>
        </div>
        {canAdmin && !adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            + Add link
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {adding && (
          <AccountLinkForm
            fromQbId={qbId}
            onSuccess={() => setAdding(false)}
            onCancel={() => setAdding(false)}
          />
        )}
        {links.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No splits, merges, or continuations recorded for this account.
          </p>
        ) : (
          <ul className="flex flex-col">
            {links.map((l) => (
              <LinkRow
                key={l.id}
                link={l}
                canRemove={canAdmin}
                onRemove={() => setRemoving(l)}
              />
            ))}
          </ul>
        )}
      </CardContent>
      <RemoveLinkDialog
        link={removing}
        open={!!removing}
        onOpenChange={(v) => !v && setRemoving(null)}
      />
    </Card>
  );
}

function LinkRow({
  link,
  canRemove,
  onRemove,
}: {
  link: AccountDetailLink;
  canRemove: boolean;
  onRemove: () => void;
}) {
  const outgoing = link.direction === "outgoing";
  const otherQb = outgoing ? link.target_qb_id : link.source_qb_id;
  const label = outgoing
    ? `${REL_LABEL_OUT[link.relationship] ?? link.relationship} →`
    : `← ${REL_LABEL_IN[link.relationship] ?? link.relationship}`;
  return (
    <li className="grid grid-cols-[max-content_1fr_max-content] items-baseline gap-3 border-b border-border py-2 text-sm last:border-b-0">
      <span className="font-semibold text-info">{label}</span>
      <span>
        QB-{otherQb}
        {link.effective_period && (
          <span className="ml-3 text-xs text-muted-foreground">
            effective {link.effective_period}
          </span>
        )}
        {link.note && (
          <span className="ml-3 text-xs italic text-muted-foreground">"{link.note}"</span>
        )}
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          by {link.created_by_name ?? "?"} · {link.created_at ? new Date(link.created_at).toLocaleString() : "?"}
        </div>
      </span>
      {canRemove && (
        <Button variant="outline" size="sm" onClick={onRemove}>
          Remove
        </Button>
      )}
    </li>
  );
}
